import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import i18next from 'i18next';

import {
  LIST_HOTEL_COMMENTS_QUERY,
  CREATE_HOTEL_COMMENT_MUTATION,
  UPDATE_HOTEL_COMMENT_MUTATION,
  DELETE_HOTEL_COMMENT_MUTATION
} from '../../pages/clients/gql';
import ConfirmationButton from '../dialogs/ConfirmationButton';
import { dispatchMessage, dispatchException } from 'helper/snackbar';

interface HotelComment {
  id: number;
  description: string;
  createdAt: string;
  createdBy: {
    name: string;
  } | null;
}

interface HotelCommentsSectionProps {
  hotelId: number;
  hotelName: string;
}

/**
 * Section component for displaying and managing hotel comments.
 * Simpler than company comments - no booking/contact relevance flags.
 */
const HotelCommentsSection: React.FC<HotelCommentsSectionProps> = ({ hotelId, hotelName }) => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingComment, setEditingComment] = useState<HotelComment | null>(null);
  const [description, setDescription] = useState('');

  const { data, loading, error, refetch } = useQuery(LIST_HOTEL_COMMENTS_QUERY, {
    variables: { hotelId },
    fetchPolicy: 'cache-and-network',
  });

  const [createComment] = useMutation(CREATE_HOTEL_COMMENT_MUTATION);
  const [updateComment] = useMutation(UPDATE_HOTEL_COMMENT_MUTATION);
  const [deleteComment] = useMutation(DELETE_HOTEL_COMMENT_MUTATION);

  const comments = data?.listHotelComments || [];

  const handleAdd = () => {
    setEditingComment(null);
    setDescription('');
    setOpenDialog(true);
  };

  const handleEdit = (comment: HotelComment) => {
    setEditingComment(comment);
    setDescription(comment.description);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingComment(null);
    setDescription('');
  };

  const handleSave = async () => {
    if (!description || description.trim().length < 10) {
      dispatchMessage(dispatch, String(i18next.t('hotel-comment-validation-min')));
      return;
    }

    try {
      if (editingComment) {
        await updateComment({
          variables: { id: editingComment.id, description: description.trim() },
        });
      } else {
        await createComment({
          variables: { hotelId, description: description.trim() },
        });
      }

      dispatchMessage(dispatch, String(i18next.t('hotel-comment-save-success')));
      refetch();
      handleClose();
    } catch (err: any) {
      dispatchException(dispatch, err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteComment({ variables: { id } });
      dispatchMessage(dispatch, String(i18next.t('hotel-comment-delete-success')));
      refetch();
    } catch (err: any) {
      dispatchException(dispatch, err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{i18next.t('hotel-comments-error-loading')}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {comments.length === 0
            ? i18next.t('hotel-comment-no-comments')
            : `${comments.length} ${comments.length === 1 ? 'Kommentar' : 'Kommentare'}`
          }
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAdd}>
          {i18next.t('hotel-comment-add')}
        </Button>
      </Box>

      {comments.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={4}
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
            backgroundColor: 'grey.50'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {i18next.t('hotel-comment-no-comments')}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('hotel-comment-created-at')}</TableCell>
                <TableCell>{i18next.t('hotel-comment-creator')}</TableCell>
                <TableCell>{i18next.t('hotel-comment-description-label')}</TableCell>
                <TableCell align="center">{i18next.t('hotel-comment-actions')}</TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {comments.map((comment: any) => (
                <TableRow key={comment.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(comment.createdAt), 'dd.MM.yyyy HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{comment.createdBy?.name || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {comment.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title={i18next.t('hotel-comment-edit')}>
                        <IconButton size="small" onClick={() => handleEdit(comment)} sx={{ color: 'primary.main' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <ConfirmationButton
                        confirmationTitle={i18next.t('hotel-comment-delete')}
                        confirmationQuestion={i18next.t('hotel-comment-delete-confirm')}
                        onConfirm={() => handleDelete(comment.id)}
                        icon={true}
                        disabled={false}
                      >
                        <Tooltip title={i18next.t('hotel-comment-delete')}>
                          <DeleteIcon fontSize="small" />
                        </Tooltip>
                      </ConfirmationButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingComment ? i18next.t('hotel-comment-edit') : i18next.t('hotel-comment-add')}
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={i18next.t('hotel-comment-description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText={i18next.t('hotel-comment-description-helper')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{i18next.t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained">{i18next.t('save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HotelCommentsSection;